import logger from 'src/logger';
import { getLongTermWebsiteStats } from 'src/services/site/analytics/analytics.service';
import { TLifeTimeStatsFormat } from 'src/types/analytics.type';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '.';
import { INITIAL_ANALYTICS_STATS } from 'src/constants/analytics.constants';
import { getSessionIdentifier, hasValidSessionIdentifier } from 'src/helpers/session.helpers';
import { CustomError } from 'src/lib/error';

type TinitialState = {
  stats: TLifeTimeStatsFormat | null;
  isStatsFetching: boolean;
};

type TUpdateSiteStatsAction = {
  type: string;
  payload: TLifeTimeStatsFormat;
};

const initialState: TinitialState = {
  stats: null,
  isStatsFetching: false,
};

const siteAnalyticsSlice = createSlice({
  name: 'analyticsStats',
  initialState,
  reducers: {
    updateSiteStats: (state, action: TUpdateSiteStatsAction) => {
      state.stats = action.payload;
      return state;
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchAnalyticsData.fulfilled, (state, action) => {
      state.stats = action.payload || INITIAL_ANALYTICS_STATS;
      state.isStatsFetching = false;
      return state;
    });
    builder.addCase(fetchAnalyticsData.pending, state => {
      state.isStatsFetching = true;
    });
    builder.addCase(fetchAnalyticsData.rejected, state => {
      state.stats = INITIAL_ANALYTICS_STATS;
      state.isStatsFetching = false;
    });
  },
});

export const fetchAnalyticsData = createAsyncThunk('siteStats/get', async (_, { getState }) => {
  try {
    const { session } = getState() as RootState;
    if (!session || !hasValidSessionIdentifier(session) || !session.domainName) {
      throw new CustomError({ errorCode: 'SessionNotFound' });
    }
    const data = await getLongTermWebsiteStats(getSessionIdentifier(session), session?.domainName);
    return data;
  } catch (err) {
    logger.reportError(err, { message: 'Error in fetchAnalyticsStatsData' });
    throw err;
  }
});

export const siteAnalyticsStatsSelector = (state: RootState) => state.siteAnalytics.stats;
export const siteAnalyticsStatsFetchingSelector = (state: RootState) => state.siteAnalytics.isStatsFetching;

export default siteAnalyticsSlice.reducer;
